import { type JSX, useEffect, useState } from 'react';

import { AnalyticsApi } from 'Api/Analytics/AnalyticsApi';
import { Button } from 'Components/Buttons/Buttons';
import { ConfirmationModal, ConfirmationModalOperationType } from 'Components/Modal/ConfirmationModal/ConfirmationModal';
import { Placeholder } from 'Components/Placeholder/Placeholder';
import { Text } from 'Components/Text/Text';
import { ICON_REFRESH } from 'Config/Icons';
import { iso8601ToUsTimestampLong } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { AnalyticsEtlMetadata, AnalyticsEtlStatus } from 'Models/Analytics';

import styles from './Analytics.module.css';

export interface AnalyticsProps {
    analyticsApi: AnalyticsApi;
}

export const Analytics = (props: AnalyticsProps): JSX.Element => {
    const [embedUrl, setEmbedUrl] = useState<string>();
    const [error, setError] = useState<string>();

    const [showRefreshConfirmationModal, setShowRefreshConfirmationModal] = useState(false);
    const [etlMetadata, setEtlMetadata] = useState<AnalyticsEtlMetadata>();

    const etlIsInProgress = etlMetadata?.status === AnalyticsEtlStatus.IN_PROGRESS;
    const etlHasFailed = etlMetadata?.status === AnalyticsEtlStatus.FAILED;

    useEffect(() => {
        const fetchEmbedUrl = async (): Promise<void> => {
            try {
                const response = await props.analyticsApi.getEmbedUrl();
                setEmbedUrl(response.data.embed_url);
            } catch (error) {
                setError(error.message);
            }
        };

        const fetchEtlMetadata = async (): Promise<void> => {
            try {
                const response = await props.analyticsApi.getEtlMetadata();
                setEtlMetadata(response.data);
            } catch (error) {
                setError(error.message);
            }
        };

        fetchEmbedUrl();
        fetchEtlMetadata();
    }, [props.analyticsApi]);

    const requestEtl = async (): Promise<string> => {
        const response = await props.analyticsApi.requestEtl();
        setEtlMetadata(response.data);
        return 'Refresh started.';
    };

    if (error) {
        return <Text color="red">{error}</Text>;
    }

    if (!(embedUrl && etlMetadata)) {
        return <Placeholder />;
    }

    // This page is not wrapped in PageLayoutDefault because the QuickSight iframe benefits from more horizontal space than our standard page layout provides.
    return (
        <>
            {showRefreshConfirmationModal && (
                <ConfirmationModal operationType={ConfirmationModalOperationType.NON_DESTRUCTIVE} headerText="Refresh Datasets" areYouSureText="Are you sure you want to refresh all datasets?" buttonText="Refresh" buttonLoadingText="Refreshing..." buttonIcon={ICON_REFRESH} performOperation={requestEtl} hideModal={() => setShowRefreshConfirmationModal(false)}>
                    <Text>All datasets will be refreshed with your organization's latest data. This may take several minutes.</Text>
                </ConfirmationModal>
            )}
            <div className={styles.refreshDatasetsHeader}>
                <Button variant="primary" onClick={() => setShowRefreshConfirmationModal(true)} fontAwesomeImage={ICON_REFRESH} disabled={etlIsInProgress}>
                    Refresh datasets
                </Button>
                {etlIsInProgress && <Text noStyles>Datasets are currently being refreshed. Please reload the page in a few minutes to view updated data.</Text>}
                {etlHasFailed && etlMetadata.last_started_timestamp && (
                    <Text noStyles color="red">
                        {`Refreshing datasets at ${iso8601ToUsTimestampLong(etlMetadata.last_started_timestamp)} failed.`}
                    </Text>
                )}
                {etlMetadata?.last_succeeded_timestamp && <Text noStyles>{`Datasets were last refreshed ${etlHasFailed ? 'successfully ' : ''}at ${iso8601ToUsTimestampLong(etlMetadata.last_succeeded_timestamp)}.`}</Text>}
            </div>
            <iframe
                title="QuickSight Dashboard"
                src={embedUrl}
                style={{
                    width: '100%',
                    height: 'calc(100vh - 210px)',
                    border: 'none',
                }}
            />
        </>
    );
};
